// ============================================================
// Analytics v2 — exact (conversion-grade) events
//
// Analytics Engine samples writes, so its counts are weighted estimates —
// fine for page views, not for what customers bill and act on. Exact events
// are written to AE as usual (funnels, sessions, attribution keep working)
// AND once, unsampled, to the DB_ANALYTICS `events` table by the pipeline
// (INSERT OR IGNORE on (project, event, id): retries never add or rewrite
// rows). Counters over exact events read that table. YOM-PLATFORM-PLAN.md §4.
// ============================================================
/** Exact for every project. Projects add more with `events.custom.<name>.exact: true`. */
export const DEFAULT_EXACT_EVENTS = [
    'purchase',
    'refund',
    'generate_lead',
    'form_submit',
    'sign_up',
    // Consent receipts (consent.ts): proof of consent must be exact, never sampled.
    'consent_update',
];
/** Derived rows follow their parent: item-level revenue is exact when purchases are. */
const DERIVED_PARENT = { purchase_item: 'purchase' };
function own(config, name) {
    return Object.prototype.hasOwnProperty.call(config.events.custom, name)
        ? config.events.custom[name]
        : undefined;
}
/** True when the pipeline must store this event in the exact table. */
export function isExactEvent(name, config) {
    const n = DERIVED_PARENT[name] ?? name;
    return DEFAULT_EXACT_EVENTS.includes(n) || own(config, n)?.exact === true;
}
/**
 * Epoch ms from which the exact table holds every occurrence of `name`, or null when it
 * can't answer for it. Default events: 0 (exact since v2 shipped, before any customer
 * data). Custom-flagged events: when the flag was saved — earlier ranges stay on AE.
 */
export function exactSince(name, config) {
    const n = DERIVED_PARENT[name] ?? name;
    if (DEFAULT_EXACT_EVENTS.includes(n)) {
        return 0;
    }
    const spec = own(config, n);
    return spec?.exact === true && spec.exactSince !== undefined ? spec.exactSince : null;
}
/**
 * Stamp `exactSince` on newly flagged custom events (keeps the stamp of events that were
 * already exact). Call on every config save; client-supplied stamps are never trusted.
 */
export function stampExactSince(next, prev, now = Date.now()) {
    for (const [name, spec] of Object.entries(next.events.custom)) {
        if (!spec.exact) {
            delete spec.exactSince;
            continue;
        }
        const before = prev ? own(prev, name) : undefined;
        spec.exactSince = before?.exact && before.exactSince ? before.exactSince : now;
    }
    return next;
}
//# sourceMappingURL=exact.js.map